import { secureGet, secureGetFile, securePut, securePost } from '../utils/HttpUtils';
import { normalizeCuitForSearch } from '../utils/Utils';

const apiUrls = {
  monetizedAvales: '/v1/private/guarantees/monetized-avales',
  monetizedAvalesExportCsv: '/v1/private/guarantees/monetized-avales/export-csv',
  reportsRiesgoVivoCsv: '/v1/private/guarantees/reports/riesgo-vivo/export-csv',
  reportsContingenteCsv: '/v1/private/guarantees/reports/contingente/export-csv',
  reportsBcraCsv: '/v1/private/guarantees/reports/bcra/export-csv',
  reportsVistaCierreCsv: '/v1/private/guarantees/reports/vista-cierre/export-csv',
  approvedAmounts: '/v1/private/applications/approved-amounts',
  approvedLines: '/v1/private/approved-lines',
  guaranteeDetail: '/v1/private/guarantees',
  productLineDetail: '/v1/private/guarantees/approved-line',
  drawerByCuit: '/v1/private/guarantees/drawers',
  overdueFilters: '/v1/private/guarantees/overdue/filters',
  overdue: '/v1/private/guarantees/overdue',
  overdueExportCsv: '/v1/private/guarantees/overdue/export-csv',
  overdueTotals: '/v1/private/guarantees/overdue/totals',
  triggerOverdue: '/v1/private/guarantees/admin/trigger-overdue',
  triggerCollectedOk: '/v1/private/guarantees/admin/trigger-collected-ok',
  overdueCollectedOk: '/v1/private/guarantees/overdue',
  overdueUncollectible: '/v1/private/guarantees/overdue',
  overdueRestore: '/v1/private/guarantees/overdue',
  guaranteeStatus: '/v1/private/guarantees/status',
  ongoing: '/v1/private/guarantees',
  guaranteeGeneralLoans: '/v1/private/guarantees/general/loans',
  guaranteeGeneralChecks: '/v1/private/guarantees/general/checks',
  guaranteeGeneralPromissoryNotes: '/v1/private/guarantees/general/promissory-notes',
  guaranteeGeneralCommercialEndorsements: '/v1/private/guarantees/general/commercial-endorsements',
  commissionTypes: '/v1/private/guarantees/commission-types',
  commissions: '/v1/private/guarantees/commissions',
  purposeTypes: '/v1/private/guarantees/purpose-types',
  riskMetrics: '/v1/private/guarantees/risk-metrics',
  riskMetricsProtectors: '/v1/private/guarantees/risk-metrics/protectors',
  dashboardAvales: '/v1/private/dashboard/avales',
  protectorDetail: '/v1/private/guarantees/protectors',
  doubleCap: '/v1/private/guarantees/double-cap',
};

export const getApprovedAmounts = async (
  page: number = 0,
  size: number = 10,
  searchText: string = '',
  filters?: Record<string, string>
) => {
  try {
    let url = `${apiUrls.approvedAmounts}?page=${page}&size=${size}`;

    if (searchText && searchText.trim() !== '') {
      let processedSearchText = searchText.trim();

      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedSearchText)) {
        processedSearchText = processedSearchText.replace(/-/g, '');
      }

      url += `&filterText=${encodeURIComponent(processedSearchText)}`;
    }

    // Agregar filtros adicionales si existen
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching approved amounts:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getApprovedLines = async (
  filterText: string = '',
  page: number = 0,
  size: number = 10
) => {
  try {
    let url = `${apiUrls.approvedLines}?page=${page}&size=${size}`;

    if (filterText && filterText.trim() !== '') {
      let processedFilterText = filterText.trim();

      const cuitPattern = /^\d{2}-\d{8}-\d{1}$/;
      if (cuitPattern.test(processedFilterText)) {
        processedFilterText = processedFilterText.replace(/-/g, '');
      }

      url += `&filterText=${encodeURIComponent(processedFilterText)}`;
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching approved lines:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeDetail = async (cuit: string) => {
  try {
    const url = `${apiUrls.guaranteeDetail}/${cuit}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getG2Header = async (cuit: string) => {
  try {
    const url = `${apiUrls.doubleCap}/cuit/${cuit}/header`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching G2 header:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getG2Products = async (cuit: string) => {
  try {
    const url = `${apiUrls.doubleCap}/cuit/${cuit}/products`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching G2 products:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getG3Header = async (cuit: string, productId: number, currencyId: number) => {
  try {
    const url = `${apiUrls.doubleCap}/cuit/${cuit}/product/${productId}/currency/${currencyId}/header`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching G3 header:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getG3Aapl = async (cuit: string, productId: number, currencyId: number) => {
  try {
    const url = `${apiUrls.doubleCap}/cuit/${cuit}/product/${productId}/currency/${currencyId}/aapl`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching G3 AAPL:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getG3Avales = async (cuit: string, productId: number, currencyId: number) => {
  try {
    const url = `${apiUrls.doubleCap}/cuit/${cuit}/product/${productId}/currency/${currencyId}/avales`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching G3 avales:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const annulMonetization = async (type: string, id: number) => {
  try {
    const url = `/v1/private/guarantees/monetization/${type}/${id}/annul`;
    return securePost(url, {});
  } catch (error) {
    console.error('Error annulling monetization:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const earlyCancellation = async (type: string, id: number, date: string) => {
  try {
    const url = `/v1/private/guarantees/monetization/${type}/${id}/early-cancellation`;
    return securePost(url, { date });
  } catch (error) {
    console.error('Error early cancellation:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const earlyCancellationInstallment = async (id: number, date: string) => {
  try {
    const url = `/v1/private/guarantees/monetization/loans/installments/${id}/early-cancellation`;
    return securePost(url, { date });
  } catch (error) {
    console.error('Error early cancellation installment:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getProductLineDetail = async (productLineId: string) => {
  try {
    const url = `${apiUrls.productLineDetail}/${productLineId}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching product line detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGroupedProductLineDetail = async (
  cuit: string,
  productLineCode: string,
  currencyId: number,
) => {
  try {
    const url = `/v1/private/guarantees/grouped/${cuit}/${productLineCode}/${currencyId}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching grouped product line detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getDrawerByCuit = async (cuit: string) => {
  try {
    const url = `${apiUrls.drawerByCuit}/${cuit}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching drawer by CUIT:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getOverdueFilters = async () => {
  const url = apiUrls.overdueFilters;
  return secureGet(url);
};

export interface OverdueQueryParams {
  page?: number;
  size?: number;
  fromDate?: string;
  toDate?: string;
  badge?: string;
  creditor?: string;
  type?: string;
  filterText?: string;
  globalIdContains?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

const buildOverdueQueryString = (params: OverdueQueryParams, includePagination = true): string => {
  const searchParams = new URLSearchParams();
  if (includePagination) {
    const page = params.page ?? 0;
    const size = params.size ?? 10;
    searchParams.set('page', String(page + 1));
    searchParams.set('size', String(size));
  }

  if (params.fromDate) searchParams.set('fromDate', params.fromDate);
  if (params.toDate) searchParams.set('toDate', params.toDate);
  if (params.badge) searchParams.set('badge', params.badge);
  if (params.creditor) searchParams.set('creditorId', params.creditor);
  if (params.type) searchParams.set('type', params.type);
  if (params.filterText?.trim()) searchParams.set('filterText', params.filterText.trim());
  if (params.globalIdContains?.trim()) {
    searchParams.set('globalIdContains', params.globalIdContains.trim());
  }
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortDirection) searchParams.set('sortDirection', params.sortDirection);

  return searchParams.toString();
};

export const getOverdue = async (
  page: number = 0,
  size: number = 10,
  fromDate?: string,
  toDate?: string,
  badge?: string,
  creditor?: string,
  type?: string,
  filterText?: string,
  globalIdContains?: string,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  const qs = buildOverdueQueryString({
    page,
    size,
    fromDate,
    toDate,
    badge,
    creditor,
    type,
    filterText,
    globalIdContains,
    sortBy,
    sortDirection,
  });
  return secureGet(`${apiUrls.overdue}?${qs}`);
};

export const exportOverdueCsv = async (params: OverdueQueryParams) => {
  const qs = buildOverdueQueryString(params, false);
  return secureGetFile(`${apiUrls.overdueExportCsv}?${qs}`);
};

export interface OverdueTotals {
  current_month: {
    total_ars: number;
    total_usd: number;
  };
  next_month: {
    total_ars: number;
    total_usd: number;
  };
}

export const getOverdueTotals = async (): Promise<OverdueTotals | null> => {
  try {
    const response = await secureGet(apiUrls.overdueTotals);
    if (response && 'data' in response) {
      return response.data as OverdueTotals;
    }
    return null;
  } catch (error) {
    console.error('Error fetching overdue totals:', error);
    return null;
  }
};

/**
 * Mark an instrument as "Collected OK" (debtor paid creditor directly).
 * This removes the instrument from management.
 * @throws Error with specific HTTP status codes:
 *   - 400: Invalid request
 *   - 404: Instrument not found
 *   - 409: Already processed (confronted/honored)
 *   - 422: Business rule violation
 */
export const markCollectedOk = async (
  globalId: number,
  type?: string,
  installmentNumber?: number
) => {
  let url = `${apiUrls.overdueCollectedOk}/${globalId}/collected-ok`;
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (installmentNumber) params.append('installment_number', installmentNumber.toString());
  const query = params.toString();
  if (query) url += `?${query}`;
  return securePost(url, {});
};

export const triggerOverdueCron = async () => {
  return securePost(apiUrls.triggerOverdue, {});
};

export const triggerCollectedOkCron = async () => {
  return securePost(apiUrls.triggerCollectedOk, {});
};

/**
 * Mark an instrument as "Uncollectible" (cannot be recovered).
 * For loans, marks all subsequent installments.
 * @throws Error with specific HTTP status codes:
 *   - 400: Invalid request
 *   - 404: Instrument not found
 *   - 422: Business rule violation
 */
export const markUncollectible = async (
  globalId: number,
  type?: string,
  installmentNumber?: number
) => {
  let url = `${apiUrls.overdueUncollectible}/${globalId}/uncollectible`;
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (installmentNumber) params.append('installment_number', installmentNumber.toString());
  const query = params.toString();
  if (query) url += `?${query}`;
  return securePost(url, {});
};

/**
 * Restore an overdue instrument by deleting confrontations and/or honors.
 * Tenant is resolved from the BFF session — never send tenant from the client.
 */
export const restoreOverdue = async (
  globalId: number,
  type: string,
  soloHonras: boolean,
  installmentNumber?: number
) => {
  let url = `${apiUrls.overdueRestore}/${globalId}/restore`;
  const params = new URLSearchParams();
  params.append('type', type);
  params.append('solo_honras', soloHonras ? 'true' : 'false');
  if (installmentNumber) {
    params.append('installment_number', installmentNumber.toString());
  }
  url += `?${params.toString()}`;
  return securePost(url, {});
};

export interface CurrencyQuotation {
  asset_code: string;
  sale_value: number;
}

export interface ConfrontationContextData {
  scenario: number;
  asset_id: number | null;
  asset_name: string | null;
  asset_code: string | null;
  asset_quotation: number | null;
  currency_quotation: number | null;
  proposed_quantity: number | null;
  pending_quantity: number | null;
  pending_amount_ars: number | null;
}

export const getConfrontationContext = async (
  globalId: number,
  installmentId?: number,
  date?: string
): Promise<ConfrontationContextData> => {
  const params = new URLSearchParams();
  params.append('global_id', globalId.toString());
  if (installmentId) params.append('installment_id', installmentId.toString());
  if (date) params.append('date', date);
  const url = `/v1/private/guarantees/confrontation-context?${params.toString()}`;
  const response = await secureGet(url);
  return response.data;
};

export const updateGuaranteeStatus = async (
  id_global: number,
  installmentNumber: number,
  status: number,
  currencyQuotations?: CurrencyQuotation[],
  options?: {
    amount?: number;
    confrontationDate?: string;
    assetId?: number;
    assetQuantity?: number;
    assetQuotation?: number;
    inputUnit?: string;
  }
) => {
  try {
    const url = apiUrls.guaranteeStatus;
    const body: Record<string, unknown> = {
      id_global,
      installment_number: installmentNumber,
      status,
    };
    if (currencyQuotations && currencyQuotations.length > 0) {
      body.currency_quotations = currencyQuotations;
    }
    if (options?.amount != null) {
      body.amount = options.amount;
    }
    if (options?.confrontationDate) {
      body.confrontation_date = options.confrontationDate;
    }
    if (options?.assetId != null) {
      body.asset_id = options.assetId;
    }
    if (options?.assetQuantity != null) {
      body.asset_quantity = options.assetQuantity;
    }
    if (options?.assetQuotation != null) {
      body.asset_quotation = options.assetQuotation;
    }
    if (options?.inputUnit) {
      body.input_unit = options.inputUnit;
    }
    return securePut(url, body);
  } catch (error) {
    console.error(
      'Error updating guarantee status:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const updateLoanRate = async (
  loanId: number,
  installmentNumber: number,
  percentagePoint: number
) => {
  try {
    const url = `/v1/private/guarantees/loans/${loanId}/rate`;
    const body = {
      installmentNumber,
      percentagePoint,
    };
    return securePut(url, body);
  } catch (error) {
    console.error('Error updating loan rate:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createPartialPayment = async (
  instrumentId: number,
  date: string,
  totalPayment: boolean,
  amount: number,
  installmentNumber: number
) => {
  try {
    const url = `/v1/private/guarantees/instruments/${instrumentId}/partial-payments`;
    const body = {
      date,
      totalPayment,
      amount,
      installment_number: installmentNumber,
    };
    return securePost(url, body);
  } catch (error) {
    console.error('Error creating partial payment:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getOngoingMonetization = async (
  cuit: string = '',
  page: number = 0,
  size: number = 10
) => {
  try {
    let url = `${apiUrls.ongoing}/ongoing?page=${page}&size=${size}`;

    if (cuit && cuit.trim() !== '') {
      const processedCuit = normalizeCuitForSearch(cuit);

      url += `&filterText=${encodeURIComponent(processedCuit)}`;
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching ongoing monetization:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeLoanDetail = async (id: string) => {
  try {
    const url = `${apiUrls.guaranteeGeneralLoans}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee loan detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeCheckDetail = async (id: string) => {
  try {
    const url = `${apiUrls.guaranteeGeneralChecks}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee check detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteePromissoryNoteDetail = async (id: string) => {
  try {
    const url = `${apiUrls.guaranteeGeneralPromissoryNotes}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee promissory note detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeCommercialEndorsementDetail = async (id: string) => {
  try {
    const url = `${apiUrls.guaranteeGeneralCommercialEndorsements}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching guarantee commercial endorsement detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getRateTypes = async () => {
  try {
    const url = '/v1/private/guarantees/rate-types/';
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching rate types:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getAmortizationTypes = async () =>
  secureGet('/v1/private/guarantees/amortization-types');

export const getPaymentFrequencyTypes = async () =>
  secureGet('/v1/private/guarantees/payment-frequency-types');

export interface CalculateLoanInstallmentsRequest {
  monetizationAmount: number;
  monetizationDate: string;
  dueDate: string;
  gracePeriod: number;
  paymentFrequencyTypeId: number;
  amortizationTypeId: number;
  percentagePoint: number;
  rateTypeId?: number;
  assetId?: number;
  assetQuantity?: number;
}

export const calculateLoanInstallments = async (
  request: CalculateLoanInstallmentsRequest,
) => securePost('/v1/private/guarantees/loans/installments/calculate', request);

export const updateRateType = async (id: number, baseValue: number) => {
  try {
    const url = `/v1/private/guarantees/rate-types/${id}`;
    const body = {
      baseValue: baseValue,
    };
    return securePut(url, body);
  } catch (error) {
    console.error('Error updating rate type:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getLoanInstallments = async (id: string) => {
  try {
    const url = `/v1/private/guarantees/monetization/loans/installments/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching loan installments:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGuaranteeWeighingData = async (
  type: 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements',
  id: string
) => {
  try {
    const url = `/v1/private/guarantees/weighing/${type}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching weighing data:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getGduWeightingFactors = async (): Promise<{ id: number; name: string }[]> => {
  const response = await secureGet('/v1/private/guarantees/gdu-weighting-factors');
  return response?.data ?? response ?? [];
};

export const recalculateCasfogWeighting = async (
  type: 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements',
  id: string
) => {
  return securePost(`/v1/private/guarantees/weighting/${type}/${id}/recalculate`, {});
};

export const applyManualWeighting = async (
  type: 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements',
  id: string,
  gduWeightingFactorId: number
) => {
  return securePost(`/v1/private/guarantees/weighting/${type}/${id}/manual`, {
    gdu_weighting_factor_id: gduWeightingFactorId,
  });
};

export const getCommissionTypes = async () => {
  try {
    const url = apiUrls.commissionTypes;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching commission types:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getCommissions = async (
  type: 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements',
  idGlobal: string
) => {
  try {
    const url = `${apiUrls.commissions}/${type}/${idGlobal}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching commissions:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createCommission = async (
  type: 'checks' | 'loans' | 'promissory-notes' | 'commercial-endorsements',
  idGlobal: string,
  commissionData: {
    commission_type_id: number;
    name: string;
    amount: number;
  }
) => {
  try {
    const url = `${apiUrls.commissions}/${type}/${idGlobal}`;
    return securePost(url, commissionData);
  } catch (error) {
    console.error('Error creating commission:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getPurposeTypes = async () => {
  try {
    const url = apiUrls.purposeTypes;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching purpose types:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getRiskMetrics = async () => {
  try {
    const url = apiUrls.riskMetrics;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching risk metrics:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getDashboardAvales = async () => {
  try {
    return secureGet(apiUrls.dashboardAvales);
  } catch (error) {
    console.error(
      'Error fetching dashboard avales:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getRiskMetricsProtectors = async (
  page: number = 0,
  size: number = 20,
  date?: string,
  sortBy?: 'amount' | 'integrated',
  sortDirection?: 'asc' | 'desc',
  filterText?: string
) => {
  try {
    let url = `${apiUrls.riskMetricsProtectors}?page=${page}&size=${size}`;

    if (date) {
      url += `&date=${encodeURIComponent(date)}`;
    }

    if (sortBy) {
      url += `&sortBy=${encodeURIComponent(sortBy)}`;
    }

    if (sortDirection) {
      url += `&sortDirection=${encodeURIComponent(sortDirection)}`;
    }

    if (filterText && filterText.trim() !== '') {
      url += `&filterText=${encodeURIComponent(filterText.trim())}`;
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching risk metrics protectors:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getProtectorDetail = async (id: string | number) => {
  try {
    const url = `${apiUrls.protectorDetail}/${id}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector detail:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const checkProtectorCanContribute = async (protectorId: string | number): Promise<boolean> => {
  try {
    const url = `${apiUrls.protectorDetail}/${protectorId}/can-contribute`;
    const response = await secureGet(url);
    if (response.success && 'data' in response) {
      return response.data.canContribute === true || response.data.can_contribute === true;
    }
    return false;
  } catch (error) {
    console.error(
      'Error checking protector contribution eligibility:',
      JSON.stringify(error, null, 2)
    );
    return false;
  }
};

export const createProtector = async (protectorData: {
  companyName: string;
  documentTypeId?: number | null;
  legalEntityTypeId?: number | null;
  cuit?: string | null;
  email?: string | null;
  meetingMinuteTypeId?: number | null;
  minuteNumber?: string | null;
  registrationDate?: string | null;
}) => {
  try {
    const url = '/v1/private/guarantees/protectors';
    return securePost(url, protectorData);
  } catch (error) {
    console.error('Error creating protector:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateProtector = async (
  id: string | number,
  protectorData: {
    companyName?: string;
    documentTypeId?: number | null;
    legalEntityTypeId?: number | null;
    cuit?: string | null;
    email?: string | null;
    meetingMinuteTypeId?: number | null;
    minuteNumber?: string | null;
    registrationDate?: string | null;
  }
) => {
  try {
    const url = `/v1/private/guarantees/protectors/${id}`;
    return securePut(url, protectorData);
  } catch (error) {
    console.error('Error updating protector:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getProtectorFormMetadata = async () => {
  try {
    const url = '/v1/private/guarantees/form-metadata/protector';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getProtectorShareReceiptFormMetadata = async (protectorId?: string | number) => {
  try {
    const params = protectorId != null ? `?protectorId=${protectorId}` : '';
    const url = `/v1/private/guarantees/form-metadata/protector-share-receipt${params}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector share receipt form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createProtectorShareReceipt = async (shareReceiptData: {
  quantity: number;
  partner_id: number;
  protector_id: number;
  minute_date: string;
  minute_number: string;
}) => {
  try {
    const url = '/v1/private/guarantees/protector-share-receipts';
    return securePost(url, shareReceiptData);
  } catch (error) {
    console.error(
      'Error creating protector share receipt:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getCapitalContributionFormMetadata = async () => {
  try {
    const url = '/v1/private/guarantees/form-metadata/capital-contribution';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching capital contribution form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getReimpositionEligibleContributions = async (
  protectorId: number,
  currencyId: number
) => {
  try {
    const url = `/v1/private/guarantees/capital-contributions/reimposition-eligible?protector_id=${protectorId}&currency_id=${currencyId}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching reimposition-eligible contributions:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createCapitalContribution = async (contributionData: {
  protector_id: number;
  contribution_date: string;
  expiration_date: string;
  currency_id: number;
  amount: number;
  fixed_available_amount: number;
  participant_shareholders_return: boolean;
  is_tax_deductible: boolean;
  authorized_fund_id: number;
  previous_contribution_id: number | null;
  minute_date: string;
  minute_number: string;
  is_reimposition?: boolean;
}) => {
  try {
    const url = '/v1/private/guarantees/capital-contributions';
    return securePost(url, contributionData);
  } catch (error) {
    console.error(
      'Error creating capital contribution:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getCapitalContributionById = async (
  contributionId: number,
  protectorId: number,
) => {
  try {
    const url = `/v1/private/guarantees/capital-contributions/${contributionId}?protectorId=${protectorId}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching capital contribution by id:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const updateCapitalContribution = async (
  contributionId: number,
  protectorId: number,
  contributionData: {
    amount: number;
    fixed_available_amount: number;
    participant_shareholders_return: boolean;
    is_tax_deductible: boolean;
    authorized_fund_id: number;
    minute_date: string;
    minute_number: string;
  },
) => {
  try {
    const url = `/v1/private/guarantees/capital-contributions/${contributionId}?protectorId=${protectorId}`;
    return securePut(url, contributionData);
  } catch (error) {
    console.error(
      'Error updating capital contribution:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getContributionAssetIntegrationFormMetadata = async () => {
  try {
    const url =
      '/v1/private/guarantees/form-metadata/contribution-asset-integration';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching contribution asset integration form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getContributionAssetIntegrations = async (
  contributionId: number
) => {
  try {
    const url = `/v1/private/guarantees/contribution-asset-integrations?contributionId=${contributionId}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching contribution asset integrations:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createContributionAssetIntegration = async (integrationData: {
  contribution_id: number;
  asset_to_integrate_id: number;
  nominal_value: number;
  price: number;
  currency_id: number;
}) => {
  try {
    const url = '/v1/private/guarantees/contribution-asset-integrations';
    return securePost(url, integrationData);
  } catch (error) {
    console.error(
      'Error creating contribution asset integration:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getPartners = async () => {
  try {
    const url = '/v1/private/guarantees/partners';
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching partners:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getPartnerFormMetadata = async () => {
  try {
    const url = '/v1/private/guarantees/form-metadata/partner';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching partner form metadata:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createPartner = async (partnerData: {
  name: string;
  cuit: string;
  share_quantity: number;
  hierarchy_level_id: number;
}) => {
  try {
    const url = '/v1/private/guarantees/partners';
    return securePost(url, partnerData);
  } catch (error) {
    console.error('Error creating partner:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updatePartner = async (
  id: number,
  partnerData: {
    name: string;
    cuit: string;
    share_quantity: number;
    hierarchy_level_id: number;
  }
) => {
  try {
    const url = `/v1/private/guarantees/partners/${id}`;
    return securePut(url, partnerData);
  } catch (error) {
    console.error('Error updating partner:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getFunds = async () => {
  try {
    const url = '/v1/private/guarantees/funds';
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching funds:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createFund = async (fundData: {
  name: string;
  is_tax_deductible: boolean;
}) => {
  try {
    const url = '/v1/private/guarantees/funds';
    return securePost(url, fundData);
  } catch (error) {
    console.error('Error creating fund:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateFund = async (
  id: number,
  fundData: {
    name: string;
    is_tax_deductible: boolean;
  }
) => {
  try {
    const url = `/v1/private/guarantees/funds/${id}`;
    return securePut(url, fundData);
  } catch (error) {
    console.error('Error updating fund:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getAssetsToIntegrate = async () => {
  try {
    const url = '/v1/private/guarantees/assets-to-integrate';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching assets to integrate:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createAssetToIntegrate = async (assetData: {
  name: string;
  description: string;
}) => {
  try {
    const url = '/v1/private/guarantees/assets-to-integrate';
    return securePost(url, assetData);
  } catch (error) {
    console.error(
      'Error creating asset to integrate:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const updateAssetToIntegrate = async (
  id: number,
  assetData: { name: string; description: string }
) => {
  try {
    const url = `/v1/private/guarantees/assets-to-integrate/${id}`;
    return securePut(url, assetData);
  } catch (error) {
    console.error(
      'Error updating asset to integrate:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getCapitalContributions = async (
  protectorId: string | number,
  page: number = 0,
  size: number = 10
) => {
  try {
    const url = `/v1/private/guarantees/capital-contributions/?protectorId=${protectorId}&page=${page}&size=${size}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching capital contributions:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getWithdrawalPreview = async (contributionId: number) => {
  try {
    const url = `/v1/private/guarantees/capital-contributions/${contributionId}/withdrawal-preview`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching withdrawal preview:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getContributionWithdrawal = async (
  contributionId: number,
  protectorId: number | string
) => {
  try {
    const url = `/v1/private/guarantees/capital-contributions/${contributionId}/withdrawal?protectorId=${protectorId}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching contribution withdrawal:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const executeWithdrawal = async (
  contributionId: number,
  body: { net_yield_ars: number; net_yield_usd: number; withdrawal_date: string }
) => {
  try {
    const url = `/v1/private/guarantees/capital-contributions/${contributionId}/withdraw`;
    return securePost(url, body);
  } catch (error) {
    console.error(
      'Error executing withdrawal:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getProtectorShareReceipts = async (
  protectorId: string | number,
  page: number = 0,
  size: number = 10
) => {
  try {
    const url = `/v1/private/guarantees/protector-share-receipts/?protectorId=${protectorId}&page=${page}&size=${size}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector share receipts:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export type ProtectorConfrontationFilters = {
  contributionIds?: number[];
  operationType?: string;
  monetizationTypeNames?: string[];
  globalIdContains?: string;
};

export type ProtectorConfrontationListOptions = ProtectorConfrontationFilters & {
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

const appendProtectorConfrontationQueryParams = (
  params: URLSearchParams,
  protectorId: string | number,
  options: ProtectorConfrontationListOptions = {},
  localCurrencyCode?: string,
) => {
  params.set('protectorId', String(protectorId));
  if (options.contributionIds?.length) {
    options.contributionIds.forEach((id) => params.append('contributionIds', String(id)));
  }
  if (options.operationType) {
    params.set('operationType', options.operationType);
  }
  if (options.monetizationTypeNames?.length) {
    options.monetizationTypeNames.forEach((name) =>
      params.append('monetizationTypeNames', name)
    );
  }
  if (options.globalIdContains?.trim()) {
    params.set('globalIdContains', options.globalIdContains.trim());
  }
  if (options.sortBy) {
    params.set('sortBy', options.sortBy);
  }
  if (options.sortDirection) {
    params.set('sortDirection', options.sortDirection);
  }
  if (localCurrencyCode) {
    params.set('localCurrencyCode', localCurrencyCode);
  }
};

export const getProtectorInstallmentConfrontations = async (
  protectorId: string | number,
  page: number = 0,
  size: number = 20,
  options: ProtectorConfrontationListOptions = {}
) => {
  try {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', String(size));
    appendProtectorConfrontationQueryParams(params, protectorId, options);
    const url = `/v1/private/guarantees/protector-installment-confrontations?${params.toString()}`;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching protector installment confrontations:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const exportProtectorInstallmentConfrontationsCsv = async (
  protectorId: string | number,
  options: ProtectorConfrontationListOptions = {},
  localCurrencyCode: string,
) => {
  const params = new URLSearchParams();
  appendProtectorConfrontationQueryParams(params, protectorId, options, localCurrencyCode);
  const url = `/v1/private/guarantees/protector-installment-confrontations/export-csv?${params.toString()}`;
  return secureGetFile(url);
};

export const getSyndicatedOperations = async (
  type: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement',
  id: string
) => {
  try {
    // Mapear el tipo de garantía al formato del endpoint
    const typeMap: Record<string, string> = {
      loan: 'loans',
      check: 'checks',
      promissory_note: 'promissory-notes',
      commercial_endorsement: 'commercial-endorsements',
    };

    const endpointType = typeMap[type] || type;
    const url = `/v1/private/guarantees/syndicated/${endpointType}/${id}`;
    console.log('Fetching syndicated operations from URL:', url);
    const response = await secureGet(url);
    console.log('Syndicated operations response:', response);
    return response;
  } catch (error: any) {
    console.error(
      'Error fetching syndicated operations:',
      JSON.stringify(error, null, 2)
    );
    console.error('Error details:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      url: error?.config?.url,
    });
    throw error;
  }
};

export const getReinsuranceInstitutions = async () => {
  try {
    const url = '/v1/private/guarantees/reinsurances/institutions';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching reinsurance institutions:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const createReinsurance = async (reinsuranceData: {
  id_global: number;
  percentage: number;
  reinsurance_institution_id: number;
}) => {
  try {
    const url = '/v1/private/guarantees/reinsurances';
    return securePost(url, reinsuranceData);
  } catch (error) {
    console.error(
      'Error creating reinsurance:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const updateReinsurance = async (
  idGlobal: number,
  reinsuranceData: {
    percentage: number;
    reinsurance_institution_id: number;
  }
) => {
  try {
    const url = `/v1/private/guarantees/reinsurances/${idGlobal}`;
    return securePut(url, reinsuranceData);
  } catch (error) {
    console.error(
      'Error updating reinsurance:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export type InstrumentReinsurancePath =
  | 'checks'
  | 'promissory-notes'
  | 'loans'
  | 'commercial-endorsements';

const INSTRUMENT_REINSURANCE_PATH_BY_GUARANTEE_TYPE: Record<
  'loan' | 'check' | 'promissory_note' | 'commercial_endorsement',
  InstrumentReinsurancePath
> = {
  loan: 'loans',
  check: 'checks',
  promissory_note: 'promissory-notes',
  commercial_endorsement: 'commercial-endorsements',
};

export const toInstrumentReinsurancePath = (
  guaranteeType: 'loan' | 'check' | 'promissory_note' | 'commercial_endorsement'
): InstrumentReinsurancePath => INSTRUMENT_REINSURANCE_PATH_BY_GUARANTEE_TYPE[guaranteeType];

export const saveInstrumentReinsurance = async (
  instrument: InstrumentReinsurancePath,
  idGlobal: string | number,
  payload: {
    reinsured: boolean;
    reinsurance_institution_id: number | null;
    percentage: number | null;
    reinsured_entity_pays: boolean | null;
  }
) => {
  try {
    const url = `/v1/private/guarantees/reinsurances/${instrument}/${idGlobal}`;
    return securePut(url, payload);
  } catch (error) {
    console.error(
      'Error saving instrument reinsurance:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

/** Response from POST /v1/private/guarantees/import/execute (MAV commercialization update) */
export interface ExecuteImportErrorResult {
  instrument_code: string;
  operation: string;
  reason: string;
  detail: string | null;
}

export interface ExecuteImportResponse {
  total_rows: number;
  checks_updated: number;
  promissory_notes_updated: number;
  not_found: number;
  error_count: number;
  errors: ExecuteImportErrorResult[];
}

export const executeGuaranteesImport = async (formData: FormData) => {
  try {
    const url = '/v1/private/guarantees/import/execute';
    return securePost(url, formData, 'multipart/form-data');
  } catch (error) {
    console.error(
      'Error executing guarantees import:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export interface EpymeImportResponse {
  total_rows: number;
  loaded_count: number;
  skipped_count: number;
  rejected_count: number;
  rejections_by_reason: Record<string, number>;
  errors: EpymeImportError[];
  inserted_count?: number;
  updated_count?: number;
  updated_decrease_count?: number;
  updated_increase_count?: number;
}

export interface EpymeImportError {
  row_number: number;
  cuit: string;
  echeck_number: string;
  monto: number;
  motivo: string;
  detalle: string;
}

export const processEpymeImport = async (file: File, sgrCuit: string) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sgrCuit', sgrCuit);
    const url = '/v1/private/guarantees/epyme/import';
    return securePost(url, formData, 'multipart/form-data');
  } catch (error) {
    console.error(
      'Error processing E-Pyme import:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const processEpymePromissoryNotesImport = async (file: File, sgrCuit: string) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sgrCuit', sgrCuit);
    return securePost('/v1/private/guarantees/epyme/import/promissory-notes', formData, 'multipart/form-data');
  } catch (error) {
    console.error(
      'Error processing E-Pyme promissory notes import:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

// ============================================================================
// Task 50596 — Installment Plan Redefinition via Excel
// ============================================================================

export interface InstallmentRedefinitionPreviewResult {
  currentInstallments: number;
  newInstallments: number;
  amortizationSum: number;
  monetizationAmount: number;
  comparisonTerm?: number;
  comparisonUnit?: string;
  isAsset?: boolean;
  difference: number;
  tolerance: number;
  warnings: string[];
}

export interface InstallmentRedefinitionApplyResult {
  insertedInstallments: number;
  overdueInstallments: number;
  pendingInstallments: number;
}

const installmentRedefinitionUrl = (loanId: number | string) =>
  `/v1/private/guarantees/loans/${loanId}/installments`;

/**
 * Downloads the Excel template pre-populated with current loan installments.
 * Returns the result as `arraybuffer`; the component wraps it in a Blob to trigger download.
 */
export const downloadInstallmentsTemplate = async (loanId: number | string) => {
  try {
    return secureGetFile(`${installmentRedefinitionUrl(loanId)}/template`);
  } catch (error) {
    console.error('Error downloading installments template:', JSON.stringify(error, null, 2));
    throw error;
  }
};

/**
 * Uploads the Excel for preview (without persisting). The backend validates structure,
 * types, and business rules (including tolerance ±) and returns a summary
 * to display in the confirmation modal.
 */
export const previewRedefinedInstallments = async (loanId: number | string, file: File) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    return securePost(
      `${installmentRedefinitionUrl(loanId)}/redefine/preview`,
      formData,
      'multipart/form-data'
    );
  } catch (error) {
    console.error('Error in installment redefinition preview:', JSON.stringify(error, null, 2));
    throw error;
  }
};

/**
 * Applies the redefinition: in a single transaction, disables current installments,
 * inserts the new ones, and invokes the overdue marking algorithm.
 */
export const applyRedefinedInstallments = async (loanId: number | string, file: File) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    return securePost(
      `${installmentRedefinitionUrl(loanId)}/redefine/apply`,
      formData,
      'multipart/form-data'
    );
  } catch (error) {
    console.error('Error applying installment redefinition:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getSyndicatedLines = async (query: string) => {
  try {
    return secureGet(`/v1/private/guarantees/syndicated-lines?q=${encodeURIComponent(query)}`);
  } catch (error) {
    console.error('Error fetching syndicated lines:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const previewSyndicatedInstallments = async (file: File, monetizationAmount: number) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    return securePost(
      `/v1/private/guarantees/syndicated-loans/installments/preview?monetization_amount=${monetizationAmount}`,
      formData,
      'multipart/form-data'
    );
  } catch (error) {
    console.error('Error previewing syndicated installments:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createSyndicatedLoan = async (metadata: Record<string, unknown>, file: File) => {
  try {
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(metadata));
    formData.append('file', file);
    return securePost('/v1/private/guarantees/syndicated-loans', formData, 'multipart/form-data');
  } catch (error) {
    console.error('Error creating syndicated loan:', JSON.stringify(error, null, 2));
    throw error;
  }
};

const loanInstallmentCreateBase = '/v1/private/guarantees/monetization/new/loans/installments';

export interface LoanInstallmentTemplateQuery {
  guaranteeNumber?: string;
  companyName?: string;
  cuit?: string;
  operationType?: string;
  monetizationAmount?: number;
  currency?: string;
}

export const downloadLoanInstallmentTemplate = async (params: LoanInstallmentTemplateQuery = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.guaranteeNumber) query.set('guarantee_number', params.guaranteeNumber);
    if (params.companyName) query.set('company_name', params.companyName);
    if (params.cuit) query.set('cuit', params.cuit);
    if (params.operationType) query.set('operation_type', params.operationType);
    if (params.monetizationAmount != null) {
      query.set('monetization_amount', String(params.monetizationAmount));
    }
    if (params.currency) query.set('currency', params.currency);
    const qs = query.toString();
    return secureGetFile(`${loanInstallmentCreateBase}/template${qs ? `?${qs}` : ''}`);
  } catch (error) {
    console.error('Error downloading loan installment template:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const previewLoanInstallments = async (
  file: File,
  monetizationAmount: number,
  issueDate?: string,
  assetId?: number,
  assetQuantity?: number,
  currencyId?: number
) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const query = new URLSearchParams({
      monetization_amount: String(monetizationAmount),
    });
    if (issueDate) query.set('issue_date', issueDate);
    if (assetId != null) query.set('asset_id', String(assetId));
    if (assetQuantity != null) query.set('asset_quantity', String(assetQuantity));
    if (currencyId != null) query.set('currency_id', String(currencyId));
    return securePost(
      `${loanInstallmentCreateBase}/preview?${query.toString()}`,
      formData,
      'multipart/form-data'
    );
  } catch (error) {
    console.error('Error previewing loan installments:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const createLoanWithInstallments = async (metadata: Record<string, unknown>, file: File) => {
  try {
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(metadata));
    formData.append('file', file);
    return securePost(`${loanInstallmentCreateBase}/with-installments`, formData, 'multipart/form-data');
  } catch (error) {
    console.error('Error creating loan with installments:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getLiquidationHistory = async (protectorId: string, page: number, size: number) => {
  try {
    return secureGet(
      `/v1/private/guarantees/protectors/${protectorId}/liquidations/history?page=${page}&size=${size}`
    );
  } catch (error) {
    console.error('Error fetching liquidation history:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const downloadLiquidationPdf = async (protectorId: string, from: string, to: string) => {
  try {
    return secureGetFile(
      `/v1/private/guarantees/protectors/${protectorId}/liquidations/pdf?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
    );
  } catch (error) {
    console.error('Error downloading liquidation PDF:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const downloadHistoricalLiquidationPdf = async (protectorId: string, historyId: number) => {
  try {
    return secureGetFile(
      `/v1/private/guarantees/protectors/${protectorId}/liquidations/history/${historyId}/pdf`
    );
  } catch (error) {
    console.error('Error downloading historical liquidation PDF:', JSON.stringify(error, null, 2));
    throw error;
  }
};

// ============================================================================
// Monetization New Request Wizard (Task: Nueva solicitud wizard)
// ============================================================================

export interface MonetizationRequestSearchResponse {
  cuit: string;
  companyName: string | null;
  cards: {
    cuit: string;
    capArs: number;
    capUsd: number;
    usedArs: number;
    usedUsd: number;
    availableArs: number;
    availableUsd: number;
  } | null;
  products: MonetizationRequestProduct[];
  message: string | null;
}

export interface MonetizationRequestProduct {
  cuit: string;
  productId: number;
  productLabel: string;
  currencyId: number;
  currencyLabel: string;
  capProduct: number;
  usedProduct: number;
  availableProduct: number;
  availableCuitCurrency: number;
  availableOperable: number;
}

export interface MonetizationRequestContextResponse {
  cuit: string;
  companyName: string | null;
  productId: number;
  productLineCode: string;
  productLabel: string;
  currencyId: number;
  currencyCode: string;
  currencySymbol: string;
  availableProduct: number;
  availableCuitCurrency: number;
  availableOperable: number;
  effectiveCapacity: number;
  applicationProductLineId: number | null;
}

/**
 * Searches for a client by CUIT and returns enabled products for monetization.
 * Returns HTTP 404 if CUIT not found, HTTP 400 if invalid format.
 */
export const searchMonetizationRequestByCuit = async (cuit: string): Promise<MonetizationRequestSearchResponse> => {
  try {
    const normalizedCuit = cuit.replace(/-/g, '');
    const url = `/v1/private/guarantees/monetization/new-request/${normalizedCuit}`;
    const response = await secureGet(url);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Search error');
  } catch (error) {
    console.error('Error searching monetization request by CUIT:', JSON.stringify(error, null, 2));
    throw error;
  }
};

/**
 * Gets the prefill context for the monetization form based on selected product.
 * Includes effective capacity (MIN of available product and available CUIT/currency).
 */
export const getMonetizationRequestContext = async (
  cuit: string,
  productId: number,
  currencyId: number
): Promise<MonetizationRequestContextResponse> => {
  try {
    const normalizedCuit = cuit.replace(/-/g, '');
    const url = `/v1/private/guarantees/monetization/new-request/${normalizedCuit}/product/${productId}/currency/${currencyId}/context`;
    const response = await secureGet(url);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.message || 'Error getting context');
  } catch (error) {
    console.error('Error getting monetization request context:', JSON.stringify(error, null, 2));
    throw error;
  }
};

/**
 * Gets monetization form metadata by product line code (for prefilled mode).
 * Returns guarantee types, creditors, and other form options.
 */
export const getMonetizationNewByCode = async (productLineCode: string) => {
  try {
    const url = `/v1/private/guarantees/monetization/new?product_line_code=${encodeURIComponent(productLineCode)}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error getting monetization form data by code:', JSON.stringify(error, null, 2));
    throw error;
  }
};

// ============================================================================
// Processing Log (E-Pyme)
// ============================================================================

export interface ProcessingLogFilters {
  dates: string[];
  types: string[];
}

export interface ProcessingLogItem {
  cuit: string;
  razonSocial: string;
  monto: number | null;
  moneda: string | null;
  motivo: string;
  detalle: string | null;
  fechaProceso: string;
}

export interface ProcessingLogSearchResult {
  content: ProcessingLogItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  totalAmount: number;
}

export const getProcessingLogFilters = async (sgrCuit: string): Promise<ProcessingLogFilters> => {
  try {
    const response = await secureGet(
      `/v1/private/guarantees/epyme/processing-log/filters?sgrCuit=${encodeURIComponent(sgrCuit)}`
    );
    if (response.success && 'data' in response) {
      return response.data as ProcessingLogFilters;
    }
    return { dates: [], types: [] };
  } catch (error) {
    console.error('Error getting processing log filters:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const searchProcessingLog = async (
  sgrCuit: string,
  date: string,
  type: string,
  page: number = 1,
  size: number = 50
): Promise<ProcessingLogSearchResult> => {
  try {
    const params = new URLSearchParams({
      sgrCuit,
      date,
      type,
      page: String(page),
      size: String(size),
    });
    const response = await secureGet(
      `/v1/private/guarantees/epyme/processing-log/search?${params.toString()}`
    );
    if (response.success && 'data' in response) {
      return response.data as ProcessingLogSearchResult;
    }
    return { content: [], page: 1, size: 50, totalElements: 0, totalPages: 0, totalAmount: 0 };
  } catch (error) {
    console.error('Error searching processing log:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const exportProcessingLog = async (sgrCuit: string, date: string, type: string) => {
  try {
    const params = new URLSearchParams({ sgrCuit, date, type });
    return secureGetFile(
      `/v1/private/guarantees/epyme/processing-log/export?${params.toString()}`
    );
  } catch (error) {
    console.error('Error exporting processing log:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getGuaranteeCommission = async (globalId: string) => {
  try {
    const url = `/v1/private/guarantees/guarantee-commission/${globalId}`;
    return secureGet(url);
  } catch (error) {
    console.error('Error fetching guarantee commission:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const recalculateGuaranteeCommission = async (
  globalId: string,
  ratePct: number,
  expensePct: number,
  referralPct: number = 0
) => {
  try {
    const url = `/v1/private/guarantees/guarantee-commission/${globalId}/recalculate`;
    return securePut(url, { rate_pct: ratePct, expense_pct: expensePct, referral_pct: referralPct });
  } catch (error) {
    console.error('Error recalculating guarantee commission:', JSON.stringify(error, null, 2));
    throw error;
  }
};

// ============================================================================
// Monetized Avales (Tab "Avales Monetizados")
// ============================================================================

export interface MonetizedAvalData {
  guarantee_number: number;
  company_name: string;
  cuit: string;
  guarantee_type: string;
  issue_date: string | null;
  monetization_date: string | null;
  status: string;
  collected: string;
  currency: string;
  gross_amount: number;
  current_balance: number;
  confronted_amount: number;
  honored_amount: number;
  operation_number: string | null;
  instrument_id: number;
  instrument_type: string;
  line_id: number | null;
  asset_id: number | null;
  asset_name: string | null;
  asset_quantity: number | null;
  asset_quotation: number | null;
}

export interface MonetizedAvalesSearchResult {
  content: MonetizedAvalData[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
}

export interface MonetizedAvalesFilterParams {
  page?: number;
  size?: number;
  guaranteeType?: string[];
  monetizationDateFrom?: string;
  monetizationDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  globalId?: string;
  cuit?: string;
  searchText?: string;
  operationNumber?: string;
}

export const getMonetizedAvales = async (
  params: MonetizedAvalesFilterParams
): Promise<MonetizedAvalesSearchResult | null> => {
  try {
    const searchParams = new URLSearchParams();
    if (params.page != null) searchParams.set('page', String(params.page));
    if (params.size != null) searchParams.set('size', String(params.size));
    if (params.guaranteeType && params.guaranteeType.length > 0) {
      params.guaranteeType.forEach(t => searchParams.append('guarantee_type', t));
    }
    if (params.monetizationDateFrom) searchParams.set('monetization_date_from', params.monetizationDateFrom);
    if (params.monetizationDateTo) searchParams.set('monetization_date_to', params.monetizationDateTo);
    if (params.dueDateFrom) searchParams.set('due_date_from', params.dueDateFrom);
    if (params.dueDateTo) searchParams.set('due_date_to', params.dueDateTo);
    if (params.globalId) searchParams.set('global_id', params.globalId);
    if (params.cuit) searchParams.set('cuit', params.cuit);
    if (params.searchText) searchParams.set('search_text', params.searchText);
    if (params.operationNumber) searchParams.set('operation_number', params.operationNumber);

    const url = `${apiUrls.monetizedAvales}?${searchParams.toString()}`;
    const response = await secureGet(url);
    if (response.success && 'data' in response) {
      return response.data as MonetizedAvalesSearchResult;
    }
    return null;
  } catch (error) {
    console.error('Error getting monetized avales:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const exportMonetizedAvalesCsv = async (
  params: MonetizedAvalesFilterParams
) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.guaranteeType && params.guaranteeType.length > 0) {
      params.guaranteeType.forEach(t => searchParams.append('guarantee_type', t));
    }
    if (params.monetizationDateFrom) searchParams.set('monetization_date_from', params.monetizationDateFrom);
    if (params.monetizationDateTo) searchParams.set('monetization_date_to', params.monetizationDateTo);
    if (params.dueDateFrom) searchParams.set('due_date_from', params.dueDateFrom);
    if (params.dueDateTo) searchParams.set('due_date_to', params.dueDateTo);
    if (params.globalId) searchParams.set('global_id', params.globalId);
    if (params.cuit) searchParams.set('cuit', params.cuit);
    if (params.searchText) searchParams.set('search_text', params.searchText);
    if (params.operationNumber) searchParams.set('operation_number', params.operationNumber);

    const url = `${apiUrls.monetizedAvalesExportCsv}?${searchParams.toString()}`;
    return secureGetFile(url);
  } catch (error) {
    console.error('Error exporting monetized avales CSV:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const exportRiesgoVivoCsv = async (fechaConsulta: string) => {
  try {
    const url = `${apiUrls.reportsRiesgoVivoCsv}?fecha_consulta=${encodeURIComponent(fechaConsulta)}`;
    return secureGetFile(url);
  } catch (error) {
    console.error('Error exporting Riesgo Vivo CSV:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const exportContingenteCsv = async () => {
  try {
    return secureGetFile(apiUrls.reportsContingenteCsv);
  } catch (error) {
    console.error('Error exporting Contingente CSV:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const exportBcraCsv = async () => {
  try {
    return secureGetFile(apiUrls.reportsBcraCsv);
  } catch (error) {
    console.error('Error exporting BCRA CSV:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const exportVistaCierreCsv = async (fechaConsulta: string) => {
  try {
    const url = `${apiUrls.reportsVistaCierreCsv}?fecha_consulta=${encodeURIComponent(fechaConsulta)}`;
    return secureGetFile(url);
  } catch (error) {
    console.error('Error exporting Vista cierre CSV:', JSON.stringify(error, null, 2));
    throw error;
  }
};
